// /* global Word */
// ═══════════════════════════════════════════════════════════════════════════
// RUN 13: LOT HEAD STYLE — section OOXML approach + Go To navigation
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, rangeDetail, RunDetail, SRS_NAV_PREFIX } from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig, LOTConfig } from '../taskpane/configService';

/** Bookmark prefix for this run */
const BM_PREFIX = SRS_NAV_PREFIX + "R13_";

/** High base for bookmark w:id to avoid conflicts with existing bookmarks */
const BM_ID_BASE = 91300;

function isTocStyle(style: string): boolean {
  const s = (style || "").replace(/\s/g, "").toLowerCase();
  return (
    /^toc\d*$/.test(s)            ||
    s.includes("tableofcontents") ||
    s.includes("contents")
  );
}

function hasRealHyperlinkElement(xml: string): boolean {
  return /<w:hyperlink\s[^>]*(r:id|w:anchor)=/i.test(xml);
}

/**
 * Applies "Default Paragraph Font" character style to every run in a
 * single <w:p> block, purely in XML — no API call needed.
 */
function applyDefaultParaFontToRuns(paraBlock: string): string {
  let result = paraBlock;

  // ── 1. Patch paragraph-mark rPr (inside <w:pPr>) ──
  result = result.replace(
    /(<w:pPr\b[^>]*>)([\s\S]*?)(<\/w:pPr>)/,
    (_pprFull: string, open: string, inner: string, close: string) => {
      if (/<w:rPr\b/.test(inner)) {
        const patchedInner = inner.replace(
          /(<w:rPr\b[^>]*>)([\s\S]*?)(<\/w:rPr>)/,
          (_f: string, rOpen: string, rInner: string, rClose: string) => {
            if (/<w:rStyle\b/.test(rInner)) {
              return rOpen +
                rInner.replace(/<w:rStyle\s+w:val="[^"]*"/, '<w:rStyle w:val="DefaultParagraphFont"') +
                rClose;
            }
            return `${rOpen}<w:rStyle w:val="DefaultParagraphFont"/>${rInner}${rClose}`;
          }
        );
        return open + patchedInner + close;
      }
      return `${open}${inner}<w:rPr><w:rStyle w:val="DefaultParagraphFont"/></w:rPr>${close}`;
    }
  );

  // ── 2. Patch every content run <w:r> ──
  result = result.replace(
    /(<w:r\b[^>]*>)([\s\S]*?)(<\/w:r>)/g,
    (_full: string, rOpen: string, rInner: string, rClose: string) => {
      if (/<w:rPr\b/.test(rInner)) {
        const patchedInner = rInner.replace(
          /(<w:rPr\b[^>]*>)([\s\S]*?)(<\/w:rPr>)/,
          (_f: string, open: string, inner: string, close: string) => {
            if (/<w:rStyle\b/.test(inner)) {
              return open +
                inner.replace(/<w:rStyle\s+w:val="[^"]*"/, '<w:rStyle w:val="DefaultParagraphFont"') +
                close;
            }
            return `${open}<w:rStyle w:val="DefaultParagraphFont"/>${inner}${close}`;
          }
        );
        return rOpen + patchedInner + rClose;
      }
      return `${rOpen}<w:rPr><w:rStyle w:val="DefaultParagraphFont"/></w:rPr>${rInner}${rClose}`;
    }
  );

  return result;
}

/**
 * Inject a bookmark into a <w:p> block so that navigation can jump to it.
 */
function injectBookmarkIntoBlock(
  block: string,
  bookmarkName: string,
  bookmarkId: number
): string {
  const bmStart = `<w:bookmarkStart w:id="${bookmarkId}" w:name="${bookmarkName}"/>`;
  const bmEnd   = `<w:bookmarkEnd w:id="${bookmarkId}"/>`;

  let result = block;

  const pprCloseIdx = result.indexOf('</w:pPr>');
  if (pprCloseIdx === -1) {
    result = result.replace(/(<w:p\b[^>]*>)/, '$1' + bmStart);
  } else {
    const insertPos = pprCloseIdx + '</w:pPr>'.length;
    result = result.substring(0, insertPos) + bmStart + result.substring(insertPos);
  }

  const lastPClose = result.lastIndexOf('</w:p>');
  result = result.substring(0, lastPClose) + bmEnd + result.substring(lastPClose);

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-run mutable state + shared context, threaded through the helpers.
// Bundling context/config/state keeps every helper within the parameter limit.
// ═══════════════════════════════════════════════════════════════════════════

interface Run13State {
  styled: number;
  alreadyCorrect: number;
  skipped: number;
  navCount: number;
  navDetails: RunDetail[];
}

interface Ctx {
  context: Word.RequestContext;
  c: LOTConfig;
  state: Run13State;
}

/** Look-ahead validation: confirm the paragraph after the heading is a real
 *  LOT entry — non-empty, starts with the configured prefix (e.g. "Table"),
 *  and contains a genuine hyperlink. Returns true if validation passes. */
async function validateLookAhead(para: Word.Paragraph, ctx: Ctx): Promise<boolean> {
  const { context, c } = ctx;

  const nextPara = para.getNextOrNullObject();
  nextPara.load("text, style");
  await context.sync();

  let lookahead     = nextPara;
  let lookaheadText = "";
  let safetyCounter = 0;

  while (!lookahead.isNullObject && safetyCounter < 10) {
    lookaheadText = (lookahead.text || "").trim();
    if (lookaheadText.length > 0) break;
    const next = lookahead.getNextOrNullObject();
    next.load("text, style");
    await context.sync();
    lookahead = next;
    safetyCounter++;
  }

  if (lookahead.isNullObject || lookaheadText.length === 0) {
    console.log(`    ✗ SKIP — no non-empty next paragraph found`);
    return false;
  }

  console.log(`    look-ahead: "${lookaheadText.substring(0, 70)}"`);

  if (!lookaheadText.startsWith(c.lookaheadPrefix)) {
    console.log(`    ✗ SKIP — next para doesn't start with "${c.lookaheadPrefix}"`);
    return false;
  }

  const nextOoxmlResult = lookahead.getOoxml();
  await context.sync();
  const hasRealLink = hasRealHyperlinkElement(nextOoxmlResult.value);
  console.log(`    hasRealHyperlink: ${hasRealLink}`);

  if (!hasRealLink) {
    console.log(`    ✗ SKIP — no real <w:hyperlink> in next paragraph`);
    return false;
  }
  return true;
}

/** Ensure the heading style exists in the document via the API (creates it
 *  with the standard font if missing). */
async function ensureHeadingStyle(ctx: Ctx): Promise<void> {
  const { context, c } = ctx;

  const lotStyle = context.document.getStyles().getByNameOrNullObject(c.headingStyle);
  lotStyle.load("isNullObject");
  await context.sync();

  if (lotStyle.isNullObject) {
    console.log(`    Creating "${c.headingStyle}" style...`);
    const newStyle = context.document.addStyle(c.headingStyle, Word.StyleType.paragraph);
    await context.sync();
    newStyle.font.name = "Times New Roman";
    newStyle.font.size = 12;
    newStyle.font.bold = true;
    await context.sync();
    console.log(`    ✓ "${c.headingStyle}" style created`);
  } else {
    console.log(`    ✓ "${c.headingStyle}" style already exists`);
  }
}

interface ParaBlockLocation {
  pOpenIdx: number;
  pCloseIdx: number;
  paraBlock: string;
}

/** Locate the enclosing <w:p>...</w:p> block for the heading text within the
 *  section OOXML. Returns null (after logging) if it can't be pinpointed. */
function locateParagraphBlock(sectionOoxml: string, actualText: string): ParaBlockLocation | null {
  let targetPos = sectionOoxml.indexOf(`>${actualText}</w:t>`);
  if (targetPos === -1) {
    targetPos = sectionOoxml.indexOf(`>${actualText}<`);
  }
  if (targetPos === -1) {
    const lowerOoxml  = sectionOoxml.toLowerCase();
    const lowerNeedle = `>${actualText.toLowerCase()}<`;
    const lowerPos    = lowerOoxml.indexOf(lowerNeedle);
    if (lowerPos !== -1) {
      targetPos = lowerPos;
      console.log(`    (matched via lowercase fallback at pos ${targetPos})`);
    }
  }
  if (targetPos === -1) {
    console.log(`    ⚠ Text "${actualText}" not found in section OOXML — skipping`);
    return null;
  }

  const beforeText = sectionOoxml.substring(0, targetPos);
  const pOpenIdx   = beforeText.lastIndexOf("<w:p ");
  if (pOpenIdx === -1) {
    console.log(`    ⚠ Could not find enclosing <w:p> — skipping`);
    return null;
  }

  const afterTarget  = sectionOoxml.substring(targetPos);
  const pCloseOffset = afterTarget.indexOf("</w:p>");
  if (pCloseOffset === -1) {
    console.log(`    ⚠ Could not find closing </w:p> — skipping`);
    return null;
  }

  const pCloseIdx = targetPos + pCloseOffset + "</w:p>".length;
  const paraBlock = sectionOoxml.substring(pOpenIdx, pCloseIdx);
  console.log(`    Paragraph block length: ${paraBlock.length}`);

  return { pOpenIdx, pCloseIdx, paraBlock };
}

/** Patch the <w:p> block: set the heading pStyle (unless already correct),
 *  apply DefaultParagraphFont to all runs, and inject a navigation bookmark. */
function patchParagraphBlock(
  paraBlock: string,
  xmlStyleCorrect: boolean,
  pStyleMatch: RegExpExecArray | null,
  actualText: string,
  ctx: Ctx
): string {
  const { c, state } = ctx;
  let patchedBlock: string = paraBlock;

  if (!xmlStyleCorrect) {
    if (pStyleMatch) {
      patchedBlock = patchedBlock.replace(
        /<w:pStyle\s+w:val="[^"]+"/,
        `<w:pStyle w:val="${c.headingStyleXml}"`
      );
    } else if (/<w:pPr\b[^>]*>/.test(patchedBlock)) {
      patchedBlock = patchedBlock.replace(
        /(<w:pPr\b[^>]*>)/,
        `$1<w:pStyle w:val="${c.headingStyleXml}"/>`
      );
    } else {
      patchedBlock = patchedBlock.replace(
        /(<w:p\b[^>]*>)/,
        `$1<w:pPr><w:pStyle w:val="${c.headingStyleXml}"/></w:pPr>`
      );
    }
  }

  // Always apply DefaultParagraphFont to all runs
  patchedBlock = applyDefaultParaFontToRuns(patchedBlock);
  console.log(`    DefaultParagraphFont applied to runs in OOXML`);

  // ── Inject navigation bookmark into the patched block ──
  const bmName = BM_PREFIX + String(state.navCount).padStart(3, "0");
  const bmId   = BM_ID_BASE + state.navCount;
  patchedBlock = injectBookmarkIntoBlock(patchedBlock, bmName, bmId);
  const noteText = xmlStyleCorrect ? "DefaultParagraphFont applied" : `→ ${c.headingStyle}`;
  state.navDetails.push(rangeDetail(bmName, actualText, noteText));
  state.navCount++;
  console.log(`    Bookmark "${bmName}" injected into OOXML`);

  return patchedBlock;
}

/** Ensure the heading style DEFINITION exists in the OOXML package, cloning it
 *  from the paragraph's previous style definition when necessary. */
function ensureStyleDefinitionInPackage(
  finalOoxml: string,
  currentXmlStyle: string,
  c: LOTConfig
): string {
  if (finalOoxml.includes(`w:styleId="${c.headingStyleXml}"`)) {
    console.log(`    ${c.headingStyleXml} style definition already in package`);
    return finalOoxml;
  }

  if (currentXmlStyle === "none") return finalOoxml;

  const oldStyleRegex = new RegExp(
    String.raw`<w:style\s[^>]*w:styleId="${currentXmlStyle}"[\s\S]*?<\/w:style>`
  );
  const oldStyleMatch = oldStyleRegex.exec(finalOoxml);
  if (oldStyleMatch) {
    const newStyleDef = oldStyleMatch[0]
      .replace(`w:styleId="${currentXmlStyle}"`, `w:styleId="${c.headingStyleXml}"`)
      .replace(/<w:name\s+w:val="[^"]*"/, `<w:name w:val="${c.headingStyle}"`);
    console.log(`    Cloned "${currentXmlStyle}" → added ${c.headingStyleXml} style definition`);
    return finalOoxml.replace(oldStyleMatch[0], oldStyleMatch[0] + newStyleDef);
  }

  console.log(`    ⚠ Could not find "${currentXmlStyle}" style definition to clone`);
  return finalOoxml;
}

/** Apply the LoT-head style to one confirmed heading paragraph: locate its
 *  OOXML block, patch style + font + bookmark, ensure the style definition is
 *  present, and write the modified section OOXML back. */
async function applyHeadingViaOoxml(
  sectionBody: Word.Body,
  para: Word.Paragraph,
  sectionIndex: number,
  ctx: Ctx
): Promise<void> {
  const { context, c, state } = ctx;

  await ensureHeadingStyle(ctx);

  // ── Get the entire section body OOXML ──
  console.log(`    → Getting section ${sectionIndex + 1} body OOXML...`);
  const sectionOoxmlResult = sectionBody.getOoxml();
  await context.sync();

  const sectionOoxml = sectionOoxmlResult.value;
  console.log(`    Section OOXML length: ${sectionOoxml.length}`);

  const actualText = para.text.trim();
  const loc = locateParagraphBlock(sectionOoxml, actualText);
  if (!loc) {
    state.skipped++;
    return;
  }

  // ── Check current XML pStyle ──
  const pStyleRe        = /<w:pStyle\s+w:val="([^"]+)"/;
  const pStyleMatch     = pStyleRe.exec(loc.paraBlock);
  const currentXmlStyle = pStyleMatch ? pStyleMatch[1] : "none";
  console.log(`    Current XML pStyle: "${currentXmlStyle}"`);

  // ── Check if XML style already correct ──
  const xmlStyleCorrect = currentXmlStyle === c.headingStyleXml;
  if (xmlStyleCorrect) {
    console.log(`    → Already ${c.headingStyleXml} in XML — will still apply DefaultParagraphFont`);
    state.alreadyCorrect++;
  }

  const patchedBlock = patchParagraphBlock(loc.paraBlock, xmlStyleCorrect, pStyleMatch, actualText, ctx);

  // ── Rebuild the section OOXML with the patched paragraph ──
  const modifiedOoxml =
    sectionOoxml.substring(0, loc.pOpenIdx) +
    patchedBlock +
    sectionOoxml.substring(loc.pCloseIdx);

  console.log(`    Modified OOXML length: ${modifiedOoxml.length} (delta: ${modifiedOoxml.length - sectionOoxml.length})`);

  // ── Ensure heading style definition exists in the package ──
  const finalOoxml = ensureStyleDefinitionInPackage(modifiedOoxml, currentXmlStyle, c);

  // ── Write the modified section OOXML back ──
  sectionBody.insertOoxml(finalOoxml, "Replace");
  await context.sync();

  if (!xmlStyleCorrect) state.styled++;
  const doneMsg = xmlStyleCorrect
    ? `DefaultParagraphFont applied to existing ${c.headingStyleXml}`
    : `Styled ${c.headingStyle} + DefaultParagraphFont`;
  console.log(`    → ${doneMsg} via section OOXML ✓`);
}

/** Process a single search hit: exact-match gate, TOC-style gate, look-ahead
 *  validation, then apply the heading style. */
async function processSearchResult(
  sectionBody: Word.Body,
  result: Word.Range,
  resultIndex: number,
  sectionIndex: number,
  phrase: string,
  ctx: Ctx
): Promise<void> {
  const { context, c, state } = ctx;

  const para = result.paragraphs.getFirst();
  para.load("text, style");
  await context.sync();

  // ── Exact paragraph match check ──
  const paraText = (para.text || "").trim().toUpperCase();
  if (paraText !== phrase.toUpperCase()) {
    console.log(`    [${resultIndex}] Skipped — not exact match: "${para.text.trim().substring(0, 50)}..."`);
    state.skipped++;
    return;
  }

  const currentStyle = para.style || "";
  console.log(`    [${resultIndex}] "${para.text.trim()}" | Style: "${currentStyle}"`);

  // ── Skip TOC entry paragraphs ──
  if (isTocStyle(currentStyle)) {
    console.log(`    [${resultIndex}] Skipped — TOC entry (style: "${currentStyle}"), not the real heading`);
    state.skipped++;
    return;
  }

  // ── Check if style already correct (API level) ──
  const styleNorm       = currentStyle.replace(/\s/g, "").toLowerCase();
  const apiStyleCorrect = styleNorm === c.headingStyleXml.toLowerCase();
  if (apiStyleCorrect) {
    console.log(`    → Already "${c.headingStyle}" (API) — will still apply DefaultParagraphFont`);
  }

  // ── Look-ahead validation ──
  const lookAheadOk = await validateLookAhead(para, ctx);
  if (!lookAheadOk) {
    state.skipped++;
    return;
  }

  await applyHeadingViaOoxml(sectionBody, para, sectionIndex, ctx);
}

/** Process one phrase within one section: run the search and feed each hit
 *  through processSearchResult. */
async function processPhraseInSection(
  sectionBody: Word.Body,
  phrase: string,
  sectionIndex: number,
  ctx: Ctx
): Promise<void> {
  const { context } = ctx;

  const results = sectionBody.search(phrase, {
    matchCase: false,
    matchWholeWord: false,
    matchWildcards: false,
  });
  results.load("items");
  await context.sync();

  if (results.items.length === 0) return;

  console.log(`\n  Section ${sectionIndex + 1}: Found "${phrase}" (${results.items.length} hit(s))`);

  for (let r = 0; r < results.items.length; r++) {
    await processSearchResult(sectionBody, results.items[r], r, sectionIndex, phrase, ctx);
  }
}

function reportRun13Results(ctx: Ctx): void {
  const { c, state } = ctx;

  console.log("\n" + "═".repeat(70));
  console.log(`Styled: ${state.styled} | Already correct (DefaultParagraphFont applied): ${state.alreadyCorrect} | Skipped: ${state.skipped}`);
  console.log(`Go To bookmarks placed: ${state.navCount}`);
  console.log("✓ List of Tables check complete!");
  console.log("═".repeat(70));

  if (state.styled > 0 || state.alreadyCorrect > 0) {
    reportResult('List of Tables Styling Complete', [
      { label: `${c.headingStyle} entries styled`, count: state.styled },
      { label: 'Already correct (DefaultParagraphFont applied)', count: state.alreadyCorrect },
      { label: 'Skipped', count: state.skipped },
      ...state.navDetails,
    ]);
  } else {
    // No LOT heading found (or all matches skipped). Still emit a summary
    // card so the user sees a zero-change entry in the QC Summary panel —
    // count-only rows (no bookmarkId/paraId) render without Go To buttons.
    const phraseList = c.searchPhrases.map(p => `"${p}"`).join(" or ");
    reportResult('List of Tables — no changes needed', [
      { label: `No ${phraseList} paragraphs found`, note: 'No changes made' },
      { label: `${c.headingStyle} entries styled`, count: 0 },
      { label: 'Skipped', count: state.skipped },
    ]);
  }
}

export async function run13() {
  // ── Read config ──
  const cfg = getConfig();
  const c   = cfg.run13_LOT;

  await Word.run(async (context) => {
    try {
      await ensureRunStyles(context, 13);
      console.log("═".repeat(70));
      console.log("RUN 13: List of Tables — LoT Head Styling (Section OOXML + Go To)");
      console.log("═".repeat(70));

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      const totalSections = sections.items.length;
      console.log(`Total sections: ${totalSections}`);

      const state: Run13State = {
        styled: 0,
        alreadyCorrect: 0,
        skipped: 0,
        navCount: 0,
        navDetails: [],
      };
      const ctx: Ctx = { context, c, state };

      for (let s = 0; s < totalSections; s++) {
        const sectionBody = sections.items[s].body;

        for (const phrase of c.searchPhrases) {
          await processPhraseInSection(sectionBody, phrase, s, ctx);
        }
      }

      await context.sync();

      reportRun13Results(ctx);

    } catch (error) {
      console.error("Error in run13:", error);
      console.error("Message:", error.message);
      safeAlert('An error occurred during List of Tables check.');
    }
  });
}